import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import getSymbolFromCurrency from "currency-symbol-map";
import {
  getSetupTypePerformance,
  getPlanAdherence,
  getReviewCoverage,
  getRiskRewardStats,
  getJournalFrequency,
  getJournalStreak,
  getJournalCoverage,
  getAccountValueMetrics,
  getOptionsExpiryPerformance,
  getTagCoOccurrence,
} from "../store/analytics/thunks";
import TradeReviewSection from "./insights/TradeReviewSection";
import RiskRewardSection from "./insights/RiskRewardSection";
import JournalSection from "./insights/JournalSection";
import AccountValueSection from "./insights/AccountValueSection";
import OptionsSection from "./insights/OptionsSection";
import TagSection from "./insights/TagSection";

export default function Insights({ user }) {
  const dispatch = useDispatch();
  const { colorMode } = useColorMode();
  const preferences = useSelector((state) => state.user.preferences);
  const analytics = useSelector((state) => state.analytics);
  const currencySymbol =
    getSymbolFromCurrency(preferences?.preferred_currency || "USD") || "$";

  useEffect(() => {
    dispatch(getSetupTypePerformance());
    dispatch(getPlanAdherence());
    dispatch(getReviewCoverage());
    dispatch(getRiskRewardStats());
    dispatch(getJournalFrequency({ period: "week" }));
    dispatch(getJournalStreak());
    dispatch(getJournalCoverage());
    dispatch(getAccountValueMetrics({ period: "all" }));
    dispatch(getOptionsExpiryPerformance());
    dispatch(getTagCoOccurrence());
  }, [dispatch]);

  return (
    <Flex
      flexDirection="column"
      height="100vh"
      backgroundColor={colorMode === "light" ? "gray.200" : "gray.800"}
    >
      <Box flex="auto" overflowX="auto" overflowY="auto">
        <VStack spacing={4} p={4} align="stretch" w="full">
          <Box>
            <Heading
              className="page-heading"
              fontSize="md"
              color={colorMode === "light" ? "gray.600" : "gray.200"}
            >
              Insights
            </Heading>
            <Text
              fontSize="xs"
              color={colorMode === "light" ? "gray.500" : "gray.400"}
            >
              Aggregated stats across your trades, reviews, journal, and account
              history.
            </Text>
          </Box>

          <TradeReviewSection
            setupTypePerformance={analytics.setupTypePerformance}
            planAdherence={analytics.planAdherence}
            reviewCoverage={analytics.reviewCoverage}
            currencySymbol={currencySymbol}
          />
          <RiskRewardSection
            riskReward={analytics.riskReward}
            currencySymbol={currencySymbol}
          />
          <JournalSection
            journalStreak={analytics.journalStreak}
            journalFrequency={analytics.journalFrequency}
            journalCoverage={analytics.journalCoverage}
          />
          <AccountValueSection
            accountValueMetrics={analytics.accountValueMetrics}
            currencySymbol={currencySymbol}
          />
          <OptionsSection optionsExpiry={analytics.optionsExpiry} />
          <TagSection tagCoOccurrence={analytics.tagCoOccurrence} />
        </VStack>
      </Box>
    </Flex>
  );
}
